import {
  ArrowRightStartOnRectangleIcon,
  ArrowTopRightOnSquareIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { type ReactNode, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { FeatureFlagType, isEnabledFeature } from '@/components/feature-flag/types';
import { type SettingsMenuItem, settingsMenuItems } from '@/components/settings/settings-sidebar';
import { useAuthCheck, useSignout } from '@/hooks/auth';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useMenuVisibility } from '@/hooks/use-menu-visibility';
import {
  findActiveMenuPath,
  type MobileMenuPage,
  type MobileMenuSection,
  mobileMenuSections,
  pushRecentMenuPath,
  searchMobileMenu,
} from './menu-items';

const RECENT_MENU_PATHS_STORAGE_KEY = 'mobile-menu-recent-paths';

const readRecentMenuPaths = (): string[] => {
  try {
    const value = JSON.parse(localStorage.getItem(RECENT_MENU_PATHS_STORAGE_KEY) ?? '[]');
    return Array.isArray(value) ? value.filter((path): path is string => typeof path === 'string') : [];
  } catch {
    return [];
  }
};

const writeRecentMenuPaths = (paths: string[]) => {
  try {
    localStorage.setItem(RECENT_MENU_PATHS_STORAGE_KEY, JSON.stringify(paths));
  } catch {
    // ストレージが使えない環境では履歴を残さない
  }
};

const useVisibleMobileMenuSections = (): MobileMenuSection[] => {
  const { data } = useAuthCheck();
  const { isEnabled } = useFeatureFlag();
  const { isVisible } = useMenuVisibility();
  const demoEnabled = isEnabledFeature(FeatureFlagType.Demo);
  const customMenus = data.user?.organization.header_menus ?? [];

  const isAvailable = (target: Pick<MobileMenuPage, 'featureType' | 'demoOnly' | 'uiFeatureFlag'>) =>
    (!target.featureType || isEnabled(target.featureType)) &&
    (!target.demoOnly || demoEnabled) &&
    (!target.uiFeatureFlag || isEnabledFeature(target.uiFeatureFlag));

  const sections = mobileMenuSections
    .filter((section) => isAvailable(section) && (!section.menuType || isVisible(section.menuType)))
    .map((section) => ({ ...section, pages: section.pages.filter(isAvailable) }));

  if (customMenus.length === 0) return sections;
  return [
    ...sections,
    {
      label: 'カスタムメニュー',
      pages: customMenus.map((menu) => ({
        label: menu.label,
        to: menu.url,
        external: !menu.url.startsWith('/'),
      })),
    },
  ];
};

const settingsActivePath = (item: SettingsMenuItem) => item.activePath ?? item.to;

const useVisibleSettingsMenuItems = () => {
  const { isEnabled } = useFeatureFlag();
  return settingsMenuItems.filter((item) => !item.featureFlag || isEnabled(item.featureFlag));
};

/** 表示中のページがメニューのページなら、最近使ったページとして記録する。 */
export const useRecordRecentMenuPage = () => {
  const { pathname } = useLocation();
  const sections = useVisibleMobileMenuSections();
  const internalPaths = sections
    .flatMap((section) => section.pages.filter((page) => !page.external))
    .map((page) => page.to);
  const activePath = findActiveMenuPath(pathname, internalPaths);

  useEffect(() => {
    if (!activePath) return;
    writeRecentMenuPaths(pushRecentMenuPath(readRecentMenuPaths(), activePath));
  }, [activePath]);
};

interface MenuLinkProps {
  page: Pick<MobileMenuPage, 'to' | 'external'>;
  className: string;
  onClick: () => void;
  children: ReactNode;
}

const MenuLink = ({ page, className, onClick, children }: MenuLinkProps) =>
  page.external ? (
    <a href={page.to} target="_blank" rel="noopener noreferrer" className={className} onClick={onClick}>
      {children}
    </a>
  ) : (
    <Link to={page.to} className={className} onClick={onClick}>
      {children}
    </Link>
  );

interface MobileMenuProps {
  onClose: () => void;
}

export const MobileMenu = ({ onClose }: MobileMenuProps) => {
  const [keyword, setKeyword] = useState('');
  const { pathname } = useLocation();
  const { data } = useAuthCheck();
  const { mutate: signout } = useSignout();
  const sections = useVisibleMobileMenuSections();
  const settingsItems = useVisibleSettingsMenuItems();

  const pages = sections.flatMap((section) => section.pages);
  const activePath = findActiveMenuPath(
    pathname,
    pages.filter((page) => !page.external).map((page) => page.to),
  );
  // カスタムメニューに設定画面のパスが登録されていても設定項目の強調が消えないよう、通常ページとは別に判定する。
  const activeSettingsPath = findActiveMenuPath(pathname, settingsItems.map(settingsActivePath));
  const recentPages = readRecentMenuPaths()
    .map((path) => pages.find((page) => page.to === path))
    .filter((page): page is MobileMenuPage => !!page);
  const searchResults = searchMobileMenu(sections, settingsItems, keyword);
  const isSearching = keyword.trim().length > 0;

  const pageLinkClass = (isActive: boolean) =>
    clsx(
      'flex items-center h-11 text-[15px]',
      isActive ? '-mx-2 px-2 rounded bg-white/15 font-bold text-white' : 'text-white/80',
    );

  return (
    <div className="fixed inset-0 z-40 overflow-y-auto bg-zinc-800 px-5 pt-20 pb-10 text-white md:hidden">
      <div className="flex flex-col gap-5">
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-white/60" />
          <input
            type="search"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="メニューを検索"
            aria-label="メニューを検索"
            className="h-11 w-full rounded border border-white/25 bg-white/5 pl-10 pr-3 text-base text-white placeholder:text-white/50 focus:border-white focus:outline-hidden"
          />
        </div>

        {isSearching ? (
          <div>
            <p className="mb-1 text-xs text-white/60">{searchResults.length}件</p>
            {searchResults.map((result) => (
              <MenuLink
                key={`${result.context}-${result.to}`}
                page={result}
                onClick={onClose}
                className="flex min-h-14 flex-col justify-center border-b border-white/15"
              >
                <span className="text-base">{result.label}</span>
                <span className="text-[13px] text-white/60">{result.context}</span>
              </MenuLink>
            ))}
            {searchResults.length === 0 && (
              <p className="py-3 text-sm text-white/60">該当するメニューがありません</p>
            )}
          </div>
        ) : (
          <>
            {recentPages.length > 0 && (
              <div>
                <p className="mb-1.5 text-xs font-medium text-white/60">最近使ったページ</p>
                <div className="flex flex-wrap gap-2">
                  {recentPages.map((page) => (
                    <MenuLink
                      key={page.to}
                      page={page}
                      onClick={onClose}
                      className={clsx(
                        'flex h-9 items-center rounded px-3 text-sm',
                        page.to === activePath
                          ? 'bg-white font-bold text-zinc-800'
                          : 'border border-white/30',
                      )}
                    >
                      {page.label}
                    </MenuLink>
                  ))}
                </div>
              </div>
            )}

            {sections.map((section) => (
              <section key={section.label}>
                <h3 className="border-b border-white/15 pb-1">{section.label}</h3>
                <div className="grid grid-cols-2 gap-x-3">
                  {section.pages.map((page) => (
                    <MenuLink
                      key={page.to}
                      page={page}
                      onClick={onClose}
                      className={pageLinkClass(page.to === activePath)}
                    >
                      {page.label}
                    </MenuLink>
                  ))}
                </div>
              </section>
            ))}

            <section>
              <h3 className="border-b border-white/15 pb-1">設定</h3>
              <div className="grid grid-cols-2 gap-x-3">
                {settingsItems.map((item) => (
                  <MenuLink
                    key={item.to}
                    page={item}
                    onClick={onClose}
                    className={pageLinkClass(settingsActivePath(item) === activeSettingsPath)}
                  >
                    {item.label}
                  </MenuLink>
                ))}
              </div>
            </section>
          </>
        )}

        <div className="flex items-center justify-between gap-3 border-t border-white/15 pt-3">
          <div className="min-w-0 text-[13px] text-white/60">
            <p className="truncate">{data.user?.name}</p>
            <p className="truncate">{data.user?.organization.name}</p>
          </div>
          <div className="flex shrink-0 gap-2">
            <a
              href="https://manual.animal-office.jp/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-10 items-center gap-1 rounded border border-white/30 px-3 text-sm"
            >
              ヘルプ
              <ArrowTopRightOnSquareIcon className="w-4" />
            </a>
            <button
              type="button"
              onClick={() => {
                onClose();
                signout();
              }}
              className="flex h-10 cursor-pointer items-center gap-1 rounded border border-white/30 px-3 text-sm"
            >
              ログアウト
              <ArrowRightStartOnRectangleIcon className="w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
